/* eslint-disable @typescript-eslint/naming-convention */
import { NodeEnvironment } from 'app/types';

interface EnvKeys {
  API_KEY: string;
  API_SECRET: string;
  API_URL: string;
  APP_ANDROID_APP_STORE_LINK: string;
  APP_IOS_APP_STORE_LINK: string;
  APP_NAME: string;
  BEAMER_PROJECT_ID: string;
  BYPASS_GATEWAY: boolean;
  CLIENT_URL: string;
  CORE_URL: string;
  DEBUG_GA: boolean;
  GATEWAY_KEY: string;
  GOOGLE_ANALYTICS_KEY: string;
  HIDE_REACT_QUERY_DEV_TOOLS: boolean;
  LOADPAY_OAUTH_CLIENT_ID: string;
  LOADPAY_OAUTH_DOMAIN: string;
  LOADPAY_OAUTH_MARKETING_URL: string;
  LOGIN_EMAIL?: string;
  LOGIN_PASSWORD?: string;
  NODE_ENV: NodeEnvironment;
  RECAPTCHA_SITE_KEY: string;
  SESSION_INACTIVITY_THRESHOLD_IN_MINUTES: number; // The time in minutes a token can be from expiring before refreshing
  SHOW_ITEM_CACHE_LIFE_IN_MINUTES: number;
  __DEV__: boolean;
}

type EnvKey = keyof EnvKeys;

const getStringKeyValue = (env: ImportMetaEnv,
  envKey: EnvKey): string => env[envKey] as string | undefined ?? '';

const getNumberKeyValue = (env: ImportMetaEnv,
  envKey: EnvKey) => {
  const value = getStringKeyValue(env,
    envKey);

  return !isNaN(Number(value)) ? Number(value) : 0;
};

const getBooleanKeyValue = (
  env: ImportMetaEnv,
  envKey: EnvKey,
  fallbackValue = false,
) => {
  const value = getStringKeyValue(env,
    envKey);

  const lowerCaseValue = value.toLowerCase();

  if (!lowerCaseValue) {
    return fallbackValue;
  }

  return value.toLowerCase() === 'true';
};

const getDeveloperLoginCredentials = (env: ImportMetaEnv, isDevEnv: boolean): Partial<EnvKeys> => {
  if (isDevEnv) {
    return {
      LOGIN_EMAIL: getStringKeyValue(env,
        'LOGIN_EMAIL'),
      LOGIN_PASSWORD: getStringKeyValue(env,
        'LOGIN_PASSWORD'),
    };
  }

  return {};
};

const getKeys = (env: ImportMetaEnv): EnvKeys => {
  const NODE_ENV = getStringKeyValue(env, 'NODE_ENV') as NodeEnvironment;
  const isDevEnv = NODE_ENV === 'development' || NODE_ENV === 'local';

  return ({
    __DEV__: isDevEnv,
    API_KEY: getStringKeyValue(env,
      'API_KEY'),
    API_SECRET: getStringKeyValue(env,
      'API_SECRET'),
    API_URL: getBooleanKeyValue(env,
      'BYPASS_GATEWAY')
      ? getStringKeyValue(env,
        'CORE_URL')
      : getStringKeyValue(env,
        'API_URL'),
    APP_ANDROID_APP_STORE_LINK: getStringKeyValue(env,
      'APP_ANDROID_APP_STORE_LINK'),
    APP_IOS_APP_STORE_LINK: getStringKeyValue(env,
      'APP_IOS_APP_STORE_LINK'),
    APP_NAME: getStringKeyValue(env,
      'APP_NAME'),
    BEAMER_PROJECT_ID: getStringKeyValue(env,
      'BEAMER_PROJECT_ID'),
    BYPASS_GATEWAY: getBooleanKeyValue(env,
      'BYPASS_GATEWAY'),
    CLIENT_URL: getStringKeyValue(env,
      'CLIENT_URL'),
    CORE_URL: getStringKeyValue(env,
      'CORE_URL'),
    DEBUG_GA: getBooleanKeyValue(env,
      'DEBUG_GA'),
    GATEWAY_KEY: getStringKeyValue(env,
      'GATEWAY_KEY'),
    GOOGLE_ANALYTICS_KEY: getStringKeyValue(env,
      'GOOGLE_ANALYTICS_KEY'),
    HIDE_REACT_QUERY_DEV_TOOLS: getBooleanKeyValue(env, 'HIDE_REACT_QUERY_DEV_TOOLS'),
    LOADPAY_OAUTH_CLIENT_ID: getStringKeyValue(env,
      'LOADPAY_OAUTH_CLIENT_ID'),
    LOADPAY_OAUTH_DOMAIN: getStringKeyValue(env,
      'LOADPAY_OAUTH_DOMAIN'),
    LOADPAY_OAUTH_MARKETING_URL: getStringKeyValue(env,
      'LOADPAY_OAUTH_MARKETING_URL'),
    NODE_ENV,
    RECAPTCHA_SITE_KEY: getStringKeyValue(env,
      'RECAPTCHA_SITE_KEY'),
    SESSION_INACTIVITY_THRESHOLD_IN_MINUTES: getNumberKeyValue(env,
      'SESSION_INACTIVITY_THRESHOLD_IN_MINUTES'),
    SHOW_ITEM_CACHE_LIFE_IN_MINUTES: getNumberKeyValue(env,
      'SHOW_ITEM_CACHE_LIFE_IN_MINUTES'),

    ...getDeveloperLoginCredentials(env, isDevEnv),
  });
};

export const keys = getKeys(import.meta.env);
